import { Box, Typography, Stack, StackProps } from '@mui/material';
import WidgetWraper from 'components/Common/WidgetWraper';
import LoaderComponent from 'components/Loader';
import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { InfoTooltip, TooltipLabel } from '../InfoTooltip';
import { buildMetricAreaLineOptions } from '../chartWidgetUtility';

type SeriesConfig = {
  key: string;
  name: string;
  color: string;
  variant?: 'area' | 'line';
};

type AreaLineChartProps = {
  title: string;
  subTitle?: string;
  value: number;
  data: Record<string, any>[] | null;
  xKey: string;
  isCost?: boolean;
  hasTooltip?: boolean;
  tooltipLabel?: TooltipLabel;
  seriesConfig: SeriesConfig[];
  xAxisLabel?: string;
  isLoading?: boolean;
  minTitleHeight?: `${number}px`;
  height?: string;
  hasFilter?: boolean;
  children?: React.ReactNode;
  childrenContainerProps?: StackProps;
  containerProps?: StackProps;
};

/* -------------------- Helpers -------------------- */

const normalizeChartData = (
  data: Record<string, any>[],
  xKey: string,
  seriesConfig: SeriesConfig[],
) => {
  const xAxis = data.map((item) => item[xKey]);

  const series = seriesConfig.map((cfg) => ({
    name: cfg.name,
    color: cfg.color,
    data: data.map((item) => Number(item[cfg.key]) || 0),
  }));

  return { xAxis, series };
};

const formatValue = (value: number, isCost: boolean, withUnitSpacing?: boolean) => {
  const space = withUnitSpacing ? ' ' : '';
  if (isCost) {
    if (value >= 1_000_000) return `$${space}${(value / 1_000_000).toFixed(1)}${space}M`;
    if (value >= 1_000) return `$${space}${(value / 1_000).toFixed(1)}${space}K`;
    return `$${space}${value}`;
  }

  // default: number
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}${space}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}${space}K`;
  return `${value}`;
};

const MetricAreaLineChart = ({
  title,
  subTitle,
  value,
  data,
  xKey,
  isCost = false,
  hasTooltip,
  tooltipLabel,
  seriesConfig,
  xAxisLabel,
  isLoading = false,
  minTitleHeight = '38px',
  height,
  hasFilter,
  children,
  childrenContainerProps,
  containerProps,
}: AreaLineChartProps) => {
  const theme = useTheme();
  const colors = theme.palette;
  const chartRef = useRef<ReactECharts>(null);

  const defaultOptions = {
    title: {
      show: true,
      textStyle: {
        color: colors?.widget?.chart?.labelColorSecondary,
        fontWeight: 'normal',
        fontSize: 16,
      },
      text: 'No data',
      left: 'center',
      top: 'center',
    },
    backgroundColor: colors?.widget?.background?.chart,
  };

  /* Resize */
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      chartRef.current?.getEchartsInstance()?.resize();
    });

    if (chartRef.current) {
      resizeObserver.observe(chartRef.current.getEchartsInstance().getDom());
    }

    return () => resizeObserver.disconnect();
  }, []);

  const chartData = useMemo(() => {
    if (!data || !data.length) return null;
    return normalizeChartData(data, xKey, seriesConfig);
  }, [data, xKey, seriesConfig]);

  const option = useMemo(() => {
    if (!chartData) {
      return defaultOptions;
    }

    return buildMetricAreaLineOptions({
      chartData,
      seriesConfig,
      colors,
      isCost,
      xAxisLabel,
      formatValue,
    });
  }, [chartData, colors, isCost, xAxisLabel, seriesConfig]);

  return (
    <WidgetWraper>
      <Box sx={{ backgroundColor: colors.widget.background.card }} {...containerProps}>
        {/* Header */}
        <Stack direction="column" gap={2} mb={2}>
          <Stack direction="row" alignItems="center">
            <Typography variant="widgetTitleNew" sx={{ color: colors.widget.text.title }}>
              <HighlightedText text={title} />
            </Typography>
            {hasTooltip && tooltipLabel && <InfoTooltip label={tooltipLabel} />}
          </Stack>
          {subTitle && (
            <Typography variant="bodyText" sx={{ color: colors.widget.text.title }}>
              {subTitle}
            </Typography>
          )}
        </Stack>

        {hasFilter && (
          <Stack direction="row" justifyContent="flex-end" mt={1} {...childrenContainerProps}>
            {children}
          </Stack>
        )}

        <Typography
          variant="metricValue"
          sx={{
            color: `${colors.widget.text.value} !important`,
          }}
        >
          {formatValue(value, isCost, true)}
        </Typography>

        {/* Chart */}
        <Box height={height ?? 350}>
          {!isLoading ? (
            <ReactECharts
              ref={chartRef}
              option={option}
              style={{ height: '100%', width: '100%' }}
              notMerge={true}
              lazyUpdate={true}
            />
          ) : (
            <LoaderComponent pt={10} />
          )}
        </Box>
      </Box>
    </WidgetWraper>
  );
};

export default MetricAreaLineChart;
